import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AgentsProvider, useAgents } from './context/AgentsContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import AdminAgents from './pages/AdminAgents';
import ProtectedRoute from './components/ProtectedRoute';
import AdminDashboard from './pages/AdminDashboard';
import LoginPage from './pages/LoginPage';
import BookingPage from './pages/BookingPage';
import ViewTicketPage from './pages/ViewTicketPage';
import AdminOverview from './pages/AdminOverview';
import AdminClients from './pages/AdminClients';
import AdminNotifications from './pages/AdminNotifications';
import AdminSettings from './pages/AdminSettings';
import AdminEmailCenter from './pages/AdminEmailCenter';
import AdminCMS from './pages/AdminCMS';
import AdminFlights from './pages/AdminFlights';
import AdminClientDetail from './pages/AdminClientDetail';
import AdminTickets from './pages/AdminTickets';
import AdminFlightSearch from './pages/AdminFlightSearch';
import { detectAppSurface } from './lib/hosts';

function AdminEntryRedirect() {
  const { status } = useAuth();
  const { agents, loading: agentsLoading } = useAgents();
  if (status === 'loading' || (status === 'authenticated' && agentsLoading)) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f1f5f9',
          color: 'var(--text-muted)',
        }}
      >
        Loading…
      </div>
    );
  }
  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace />;
  }
  if (agents.length === 0) {
    return <Navigate to="/admin/agents" replace />;
  }
  return <Navigate to={`/admin/agent/${agents[0]!.id}`} replace />;
}

function SubdomainRoutes() {
  const surface = detectAppSurface();

  if (surface === 'booking') {
    return (
      <Routes>
        <Route path="/" element={<BookingPage />} />
        <Route path="/ticket/:ticketNumber" element={<ViewTicketPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      >
        <Route path="agents" element={<AdminAgents />} />
        <Route path="agent/:agentId" element={<AdminOverview />} />
        <Route path="agent/:agentId/flights" element={<AdminFlights />} />
        <Route path="agent/:agentId/flight-search" element={<AdminFlightSearch />} />
        <Route path="agent/:agentId/clients/:clientId" element={<AdminClientDetail />} />
        <Route path="agent/:agentId/clients" element={<AdminClients />} />
        <Route path="agent/:agentId/notifications" element={<AdminNotifications />} />
        <Route path="agent/:agentId/emails" element={<AdminEmailCenter />} />
        <Route path="agent/:agentId/tickets" element={<AdminTickets />} />
        <Route path="agent/:agentId/cms" element={<AdminCMS />} />
        <Route path="agent/:agentId/settings" element={<AdminSettings />} />
        <Route index element={<AdminEntryRedirect />} />
      </Route>
      <Route path="/booking" element={<BookingPage />} />
      <Route path="/ticket/:ticketNumber" element={<ViewTicketPage />} />
      {/* Payment checkout: static OAF at /payment/index.html?q=… (see static/payment/, setupProxy.js) */}
      <Route path="/" element={<AdminEntryRedirect />} />
      <Route path="*" element={<AdminEntryRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AgentsProvider>
          <SubdomainRoutes />
        </AgentsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
